import React, { useMemo } from 'react';
import { Accordion, Box, Copy, Optional, useAccordion } from '@honda-canada/design-system-react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import themeStyles from './Accordion.style';
import AccordionContent from './AccordionContent';
import getProductLinesInfo from '../../utils/getProductLinesInfo';
import getProductLineDetails from '../../utils/getProductLineDetails';
import { PRODUCT_LINE_LOOKUP_PROP_NAME } from '../../utils/constants';
import { PRODUCTLINES_CARRIED, HONDA_FINANCIAL_SERVICES } from '../../utils/gtmTags';
import usePushAccordionGtmEvents from '../../hooks/usePushAccordionGtmEvents';

const AccordionTitle = themeStyles.apply(Copy, 'AccordionTitle');
const Container = themeStyles.apply(Box, 'Container');

const DealerAccordion = ({ productLines }) => {
  const { t } = useTranslation();

  const memoProductLinesData = useMemo(() => {
    const productLinesInfo = getProductLinesInfo(productLines);
    const categoriesLookUp = getProductLineDetails(productLines, PRODUCT_LINE_LOOKUP_PROP_NAME.categories);
    const financialServicesLookUp = getProductLineDetails(productLines, PRODUCT_LINE_LOOKUP_PROP_NAME.financialOptions);
    const filterProductLinesInfo = productLinesInfo.filter(
      ({ productLineName }) => financialServicesLookUp?.[productLineName],
    );
    return { productLinesInfo, categoriesLookUp, financialServicesLookUp, filterProductLinesInfo };
  }, [productLines]);

  const ProductLineAccordionItem = [
    {
      key: 'productLines',
      title: (
        <AccordionTitle data-testid="cy-dealer-productLine-accordion">
          {t('Sub-apps.Dealers.productLinesCarriedLabel')}
        </AccordionTitle>
      ),
      content: (
        <AccordionContent
          productLines={memoProductLinesData.productLinesInfo}
          subItems={memoProductLinesData.categoriesLookUp}
        />
      ),
    },
  ];

  const FinancialServicesAccordionItem = [
    {
      key: 'financial',
      title: (
        <AccordionTitle data-testid="cy-dealer-financialServices-accordion">
          {t('Sub-apps.Dealers.financialServicesLabel')}
        </AccordionTitle>
      ),
      content: (
        <AccordionContent
          productLines={memoProductLinesData.filterProductLinesInfo}
          subItems={memoProductLinesData.financialServicesLookUp}
        />
      ),
    },
  ];

  const productLinesBehaviour = useAccordion(ProductLineAccordionItem);
  const { activeItems: productLinesActiveItems } = productLinesBehaviour;

  const financialServicesBehaviour = useAccordion(FinancialServicesAccordionItem);
  const { activeItems: financialServicesActiveItems } = financialServicesBehaviour;

  usePushAccordionGtmEvents(productLinesActiveItems, PRODUCTLINES_CARRIED);
  usePushAccordionGtmEvents(financialServicesActiveItems, HONDA_FINANCIAL_SERVICES);

  if (!productLines?.length) return null;

  return (
    <Container>
      {Object.entries(memoProductLinesData.categoriesLookUp).length > 0 && (
        <Accordion items={ProductLineAccordionItem} behaviour={productLinesBehaviour} />
      )}
      <Optional when={!!Object.entries(memoProductLinesData.financialServicesLookUp).length}>
        <Accordion items={FinancialServicesAccordionItem} behaviour={financialServicesBehaviour} />
      </Optional>
    </Container>
  );
};

DealerAccordion.propTypes = {
  productLines: PropTypes.arrayOf(
    PropTypes.shape({
      categories: PropTypes.arrayOf(PropTypes.string),
      financialOptions: PropTypes.arrayOf(PropTypes.string),
      name: PropTypes.string,
    }),
  ),
};

export default DealerAccordion;
